import { useState, useEffect, useRef, useCallback } from 'react';
import dynamic from 'next/dynamic';

import { setCookie, obtainVoucherPrices, getCookie, COOKIES, eraseCookie, handleStorage, getFixedPrice } from 'utils/helpers';

import { addToBag, getCrossSelling } from 'actions/products';
import { useI18n } from 'context/i18n';
import { useUser } from 'context/user';
import { dataLayerCartClick, dataLayerHandleEvent } from 'utils/dataLayers';

import { MAX_BAG_LENGTH } from 'utils/constants/products';
import { AUTO_BONDS } from 'utils/constants/cookies';
import { SIZE_GUIDE_WITH_MODELS } from 'utils/constants/sizes';

import { setBagCookies } from 'utils/bag';
import { useProductSheet } from 'context/product-sheet';
import { useIsIpad } from 'hooks/useIsIpad';
import { ProductBrands } from '../productPurchaseCol/productBrands';
import { useLocale } from 'hooks';

import { Skeleton } from 'components/ui/skeleton';
import { Select, SelectItem } from 'components/ui/select';
import { Button } from 'components/ui/button';
import { LabProductInfoSection } from './lab-product-info-section';
import { useEventListener } from 'hooks/useEventListener';
import type { ProductSheet } from 'types/product';

const SizesGuide = dynamic(() => import('components/sizesGuide/lab-size-guide').then((mod) => mod.LabSizeGuide));
const NotifyMeDrawer = dynamic(() => import('components/notifyMeButton/notify-me-drawer').then((mod) => mod.NotifyMeDrawer));
const AfterpayButton = dynamic(() => import('components/checkout/afterpayButton').then((mod) => mod.default));
const PreSaleModal = dynamic(() => import('components/pdp/pre-sale-modal').then((mod) => mod.PreSaleModal));

type LabPurchaseColProps = {
  recalculating?: boolean;
  voucherApplied?: boolean;
  voucherPriceText?: string;
  usrhashValue?: string;
  currentProductSheet?: ProductSheet;
};

export function LabPurchaseCol({
  recalculating = false,
  voucherApplied = false,
  voucherPriceText,
  usrhashValue = null,
  currentProductSheet: productSheet,
}: LabPurchaseColProps) {
  const {
    t,
    bagLength,
    profileData,
    setBagLength,
    bagHasMembersOnlyProduct,
    bag,
    setBag,
    setRecommendedProductsForBag,
    setPromoLabel,
    setPromoLabelType,
    storeData,
    disabledProducts,
  } = useI18n();
  // derived state from profileData
  const { config } = profileData;
  const showFreeShipping = config?.show_free_shipping || false;
  const showGiftPurchase = config?.show_gift_purchase || false;

  const { locale, language, country } = useLocale();
  const { userData, increaseShouldRecalculateVouchers, contextFilters, activatedPrevouchers } = useUser();

  const nameAndPriceWrapper = useRef<HTMLDivElement>(null);
  const addToCartWrapper = useRef<HTMLDivElement>(null);
  // derived state from productSheet
  const availablePrice = productSheet.prices && productSheet.prices?.current && productSheet?.notifyMeType !== 'OUT';
  const { isAccessory, sizes, code, prices, camperlab, notifyMe } = productSheet;
  const isAvailableAccesory = isAccessory && availablePrice && sizes && sizes.length > 0 ? sizes.filter((size) => size.available).length > 0 : false;
  const isMembersOnly = productSheet?.membersOnly || false;
  const defaultSelectedSizeValue = isAccessory && isAvailableAccesory ? (sizes[0]?.value ?? ' ') : undefined;
  // const isSock = productSheet.sizeType === SOCKS_TYPE;

  // const isMounted = useIsMounted();

  // const [isMouseOverCartBtn, setIsMouseOverCartBtn] = useState(false);
  const [cartError, setCartError] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState(isAccessory && isAvailableAccesory ? (sizes[0]?.value ?? ' ') : undefined);
  const [loading, setLoading] = useState(false);
  const [defaultSelectedSize] = useState(defaultSelectedSizeValue);
  const appmode = useIsIpad();
  const titleRef = useRef(null);

  // const limitedSizes = sizes && sizes.filter((size) => size.quantity === 1).map((item) => item.value);
  const selectedSizeItem = sizes && sizes.filter((size) => size?.value === selectedSize);
  const selectedSizePrice = selectedSizeItem.length && selectedSizeItem[0].price?.current;
  const totalPrice = prices !== null ? selectedSizePrice || prices?.current || prices?.previous : null;
  const availableSizes = sizes.filter((item) => item.available).map((size) => size.value);
  const isBuyingDisabled = disabledProducts === code;

  const handleSelectSize = async (size) => {
    setSelectedSize(size);

    // puesto que productSheet no tiene control sobre selectedSize
    // necesito hacer esto para leer selectedSize mientras calculo vouchers
    // así puedo cambiar los perSize una vez se selecciona la talla
    window.selectedSizeForVoucher = size;
    increaseShouldRecalculateVouchers();

    setCartError(null);

    dataLayerHandleEvent({
      event: 'item_size_selection',
      event_category: 'product detail',
      event_detail_1: 'size selection',
      event_detail_2: size,
    });
  };

  const addToCartHandler = async ({ payload, cookiesBagId, showJustAdded }) => {
    const cookiesCountry = getCookie(COOKIES.BAG_MARKET);
    const cookiesShipment = getCookie(COOKIES.SHIPMENT);
    const cookiesBonds = getCookie(COOKIES.BONDS);
    const cookiesZip = getCookie(COOKIES.ZIP);
    const cookiesEmployeeId = getCookie(COOKIES.EMPLOYEE_ID);
    const cookiesPackstation = getCookie(COOKIES.PACKSTATION);
    const cookiesCtr = getCookie(COOKIES.IS_CTR);
    if (cookiesCountry !== country) {
      payload.hashOrderId = '';
      if (cookiesBagId) {
        eraseCookie(COOKIES.BAG);
        eraseCookie(COOKIES.BAG_DATE);
      }
      if (cookiesZip) {
        eraseCookie(COOKIES.ZIP);
      }
      if (cookiesShipment) {
        eraseCookie(COOKIES.SHIPMENT);
      }
      if (cookiesCountry !== null && cookiesBonds) {
        eraseCookie(COOKIES.BONDS, COOKIES.DOMAIN);
        eraseCookie(COOKIES.AUTO_BONDS);
      }
      if (cookiesEmployeeId) {
        eraseCookie(COOKIES.EMPLOYEE_ID);
      }
      if (cookiesPackstation) {
        eraseCookie(COOKIES.PACKSTATION);
      }
      if (cookiesCtr) {
        eraseCookie(COOKIES.IS_CTR);
      }
    }

    const [result, crossSellingProducts] = await Promise.all([addToBag({ ...payload }), getCrossSelling(null, locale, payload.id)]);
    let updatedPromoLabel = null;
    let updatedPromoLabelType = null;
    const previousGridUrl = sessionStorage.getItem('gridUrl');

    if (productSheet?.baseUrl && previousGridUrl === undefined) {
      const [domain, fullUrl] = productSheet?.baseUrl?.split(locale);

      try {
        const splitUrl = fullUrl.split('/').filter(Boolean);
        const gridUrl = splitUrl.slice(0, splitUrl.length - 1).join('/');
        handleStorage('setItem', 'gridUrl', `${domain}${locale}/${gridUrl}`);
      } catch (e) {
        // en caso de que algo fallase mandamos a la home
        handleStorage('setItem', 'gridUrl', `${domain}${locale}/`);
      }
    }

    dataLayerCartClick({ ...payload, productSheet }, result, false, bag);
    if (result.hashOrderId) {
      setBagCookies({ hash: encodeURI(result.hashOrderId), market: country });

      const { sapActive, extendedBlockSAP, shoppingCart } = result;
      if (!sapActive && !extendedBlockSAP) {
        // can't pay
        window.location.assign(`${window.location.origin}/${locale}/pedido_offline`);
      } else {
        if (extendedBlockSAP) {
          // can pay
          setCookie(COOKIES.SHIPMENT, '01', undefined, COOKIES.DOMAIN);
        }
        if (!showJustAdded) {
          window.location.assign(`${window.location.origin}/${locale}/bolsa`);
        }
        if (showJustAdded) {
          const { price } = shoppingCart[0]; // siempre es una array con un solo item
          obtainVoucherPrices({ ...shoppingCart[0], prices: price }, t, locale, contextFilters, activatedPrevouchers, false, true);
          const newBag = bag ? JSON.parse(JSON.stringify(bag)) : [];
          // handleStorage('setItem', 'justAddedItem', JSON.stringify({ ...result.shoppingCart[0], membersOnly: productSheet?.membersOnly }));
          setBag(() => {
            const n = [{ ...result.shoppingCart[0], membersOnly: productSheet?.membersOnly }, ...newBag];
            return n;
          });
          setBagLength(() => {
            const newLength = bagLength + 1;
            return newLength;
          });

          setRecommendedProductsForBag((prev) => [...crossSellingProducts, ...prev].map((item, i) => ({ ...item, order: i })));

          if (showFreeShipping) {
            updatedPromoLabel = result?.freeShippingLabel || null;
          }
          // let promoLabelObtained = false;
          if (showGiftPurchase) {
            updatedPromoLabel = result?.gwplabel || result?.freeShippingLabel || null;
            if (result?.gwplabelObtained && result?.gwplabelObtained !== '') {
              updatedPromoLabel = result?.gwplabelObtained;
              // promoLabelObtained = true;
              updatedPromoLabelType = 'obtained';
            }
          }
          setLoading(false);
          setPromoLabel(updatedPromoLabel);
          setPromoLabelType(updatedPromoLabelType);
        }
      }
      // [TODO]: que quiten la cache de bolsa.json en integra
      // router.push('/bolsa');
    } else {
      setLoading(false);
      // [TODO]: handle error
      // eslint-disable-next-line no-alert
      alert('No se puede procesar el pedido en este momento');
    }
  };

  const handleClickAddToCartBtn = async (showJustAdded = false, userIdentified = false) => {
    if (isBuyingDisabled) return;
    const isUserIdentified =
      getCookie(COOKIES.SELLIGENT_USRHASH) || getCookie(COOKIES.MEMBERS_ONLY_SUBSCRIBED) || userIdentified || bagHasMembersOnlyProduct || userData !== null;

    setCartError(null);
    if (!availableSizes.includes(selectedSize)) {
      //TODO: notifyme
      const message = !isAccessory ? 'elige.talla' : 'producto.nodisponible';
      setCartError(t('ficha.producto', message));
      // check if window is less than 768px
      const mobileWindow = window.innerWidth < 768;
      if (!isAccessory && mobileWindow) {
        if (titleRef.current) {
          // scroll item to the top of the screen
          titleRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    } else if (bagLength >= MAX_BAG_LENGTH) {
      setCartError(
        t('ficha.producto', 'alcanzado.limite.bolsa', 'The product could not be added. You have reached the limit of products in your shopping cart.'),
      );
    } else if (isMembersOnly && !isUserIdentified) {
      window.showMembersOnlyModal?.('pdp_members_only_popup', undefined, undefined, async () => {
        await handleClickAddToCartBtn(true, true);
      });
    } else {
      const cookiesBagId = getCookie(COOKIES.BAG);
      setLoading(showJustAdded);
      const sizeLabel = !isAccessory ? sizes.filter((size) => size?.value === selectedSize)[0]?.label : selectedSize;
      const autoVoucher = getCookie(COOKIES.AUTO_BONDS) || '';
      const voucher = getCookie(COOKIES.BONDS) || '';
      const voucherDiscount = autoVoucher && autoVoucher === AUTO_BONDS.APPLY;
      let pricesPayload = prices;

      const discountStrToNumber = prices?.discountStr ? Number(prices?.discountStr?.replace(/\D/g, '')) : 0;
      if (prices?.discount && discountStrToNumber > prices.discount) {
        // tenemos que enviar el discount que mas beneficie
        pricesPayload.discount = discountStrToNumber;
      }

      if (prices?.discount && prices.pricePerSize) {
        const sizeFound = selectedSize ? sizes.find((size) => size?.value === selectedSize) : null;

        if (sizeFound) {
          pricesPayload = sizeFound?.price;
        }
      }

      const payload: {
        id: string;
        price: object;
        profile: object;
        size: string;
        sizeLabel: string;
        gender: string;
        thumbnail: string;
        voucher?: string;
        hashOrderId?: string;
        image?: string;
      } = {
        id: code,
        price: pricesPayload,
        profile: {
          countryId: country,
          languageId: language,
        },
        size: selectedSize,
        sizeLabel,
        gender: productSheet.realTarget || productSheet.target || '',
        thumbnail: productSheet.imageBag || '', // en ocasiones queda como null y causa un error
      };
      if (voucherDiscount || voucher) {
        payload.voucher = voucher;
      }
      if (cookiesBagId) {
        payload.hashOrderId = decodeURI(cookiesBagId);
      }

      addToCartHandler({
        payload,
        cookiesBagId,
        showJustAdded,
      });
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    });
    const urlParams = new URLSearchParams(window.location.search);
    const sizeValue = urlParams.get('size');
    const sizeIsAvailable = sizes && sizes.filter((size) => size.value === sizeValue && (size.available || notifyMe)).length > 0;
    if (sizeValue && sizeIsAvailable) {
      setSelectedSize(sizeValue);
    } else {
      setSelectedSize(defaultSelectedSize);
    }
  }, []);

  const sizeFound = sizes.find((size) => size?.value === selectedSize);

  const isPresale = productSheet.presales;

  const priceRange =
    prices?.priceDiscountFromFormatted && prices?.priceDiscountToFormatted ?
      `${prices.priceDiscountFromFormatted} - ${prices.priceDiscountToFormatted}`
    : `${prices?.priceFromFormatted} - ${prices?.priceToFormatted}`;

  const notifymeBySize = notifyMe && productSheet?.notifyMeType === 'SIZE';

  let shouldShowSizes = availablePrice;
  let shouldShowButton =
    availablePrice &&
    (!productSheet.notifyMe || (productSheet.notifyMe && notifymeBySize && (availableSizes.includes(selectedSize) || selectedSize === undefined)));
  // si el producto tiene talla unica, es notifyMe por talla y no tiene disponibilidad, no debería mostrar nada
  if (productSheet.notifyMe && productSheet.notifyMeType === 'SIZE' && sizes.length === 1 && sizes.every((size) => !size.available)) {
    shouldShowSizes = false;
    shouldShowButton = false;
  }

  if (availableSizes.length === 0 && !productSheet?.notifyMe) {
    // NO es notifyme y no tiene disponibilidad de tallas, debería ser como si no tuviese precio
    shouldShowSizes = false;
    shouldShowButton = false;
  }

  const sizeGuideInfo = SIZE_GUIDE_WITH_MODELS.includes(productSheet.code) && productSheet.description ? productSheet.description : null;
  const isStandardNotifyMe = productSheet?.notifyMeType !== null && productSheet?.notifyMeType !== 'SIZE';
  const isBySizeWithNoSizes = !isStandardNotifyMe && availableSizes.length === 0;
  // const hideFewStockMessage = isStandardNotifyMe || isBySizeWithNoSizes;
  const fixedPrice = getFixedPrice(country, profileData, prices?.current);

  // const countriesWithSizeSelector = ['US'];
  const manualYellowBox = Boolean(productSheet.textDiscountCollectionOld && !usrhashValue);
  const autoYellowBox = ![null, undefined, ''].includes(voucherPriceText);

  const onWindowResize = useCallback(() => {
    const nameNode = nameAndPriceWrapper.current;
    const addToCartNode = addToCartWrapper.current;
    if (nameNode) {
      document.body.style.setProperty('--product-header-height', `${nameNode.offsetHeight}px`);
    }
    if (addToCartNode) {
      document.body.style.setProperty('--add-to-cart-height', `${addToCartNode.offsetHeight}px`);
    }
  }, []);

  useEventListener('resize', onWindowResize);

  return (
    <div className="flex h-full flex-col px-8 pt-2.5 lab:max-w-none lab:max-lg:px-2.5 md:px-0 md:pt-0 lab:md:pt-2.5 lab:lg:pt-0">
      <div className="md:sticky md:top-header">
        <div className="">
          <div className="flex flex-col items-start justify-between gap-2 lg:gap-0">
            <div
              id="node"
              ref={(node) => {
                if (node) {
                  nameAndPriceWrapper.current = node;
                  document.body.style.setProperty('--product-header-height', `${node.offsetHeight}px`);
                }
              }}
              className="flex w-full items-start justify-between gap-1 lg:flex-col lg:justify-start lg:gap-0"
            >
              <div>
                <h1 className="mb-0 gap-1 text-sm uppercase lg:text-xs">
                  <span className="flex flex-row">
                    {productSheet.brand}
                    {(appmode || storeData) && <span>{productSheet.code}</span>}
                  </span>
                  {productSheet.codeDescription ?
                    <span dangerouslySetInnerHTML={{ __html: productSheet.codeDescription }} className="block text-sm uppercase lg:text-xs" />
                  : null}
                </h1>
              </div>
              <div className="flex-shrink-0">
                {productSheet?.notifyMeType !== 'OUT' && (
                  <>
                    {recalculating ?
                      <Skeleton className="h-4 w-8" />
                    : <p
                        // className={availablePrice ? `${styles.productCurrentPrice} ${styles.priceSpan}` : styles.productCurrentPriceNotify}
                        className="mb-0 text-sm font-bold uppercase lg:text-xs"
                        data-ge-price="data-ge-price"
                        data-fp={fixedPrice}
                      >
                        {/* es horrible y me sabe mal pero la lógica es funcional y es para ir rápido */}
                        {prices !== null && prices.pricePerSize ?
                          !availableSizes.includes(selectedSize) ?
                            priceRange
                          : sizeFound.price.currentFormated
                        : null}
                        {prices !== null && !prices.pricePerSize && productSheet.prices.currentFormated}
                      </p>
                    }
                  </>
                )}
              </div>
              <div className="basis-full empty:hidden lg:basis-auto">
                {!recalculating &&
                  availablePrice &&
                  productSheet.prices &&
                  (productSheet.prices.previous || productSheet.prices.priceDiscountFromFormatted) && (
                    <p className="mb-0 flex flex-row flex-wrap gap-1 text-xs text-disabled lg:text-xxs">
                      <span>{`${productSheet?.prices?.discountStr || `-${productSheet?.prices?.discount ?? productSheet?.prices?.discountLocal}%`}`}</span>
                      <del data-fp={getFixedPrice(country, profileData, productSheet?.prices?.previous)}>
                        {prices !== null && prices.pricePerSize ?
                          !availableSizes.includes(selectedSize) ?
                            `${prices.priceFromFormatted} - ${prices.priceToFormatted}`
                          : sizes.find((size) => size?.value === selectedSize)?.price?.previousFormated
                        : null}
                        {prices !== null && !prices.pricePerSize && productSheet.prices.previousFormated}
                      </del>
                      {!recalculating &&
                        voucherApplied &&
                        productSheet.prices?.previous !== undefined &&
                        productSheet.prices?.previous > productSheet.prices?.current &&
                        voucherPriceText === null && <span>{productSheet.prices?.appliedLabel}</span>}
                    </p>
                  )}
                {prices?.pricePerSize && (
                  <p className="mb-0 text-xs text-disabled lg:text-xxs">{t('listado.productos', 'final.price.acording', 'Final price acording to size')}</p>
                )}
                {!recalculating && !availablePrice ?
                  <p className="mb-0 text-xs uppercase text-disabled lg:text-xxs">{t('ficha.producto', 'todo.vendido')}</p>
                : null}
              </div>
            </div>
            <div className="mt-2.5 text-sm empty:hidden lg:text-xs" dangerouslySetInnerHTML={{ __html: productSheet.description }} />
            {country === 'AU' && availablePrice ?
              <AfterpayButton totalPrice={totalPrice} currency={prices.currency || 'AUD'} />
            : null}
          </div>

          {productSheet.presales && productSheet.presalesDate && (
            <div className="mt-10 text-sm lg:mt-5 lg:text-xs">
              <p className="mb-0 text-sm lg:text-xs">
                {t('ficha.producto', 'presales.estimated.date', '***Fecha estimada de entrega:')} {productSheet.presalesDate}
              </p>
              <PreSaleModal />
            </div>
          )}

          {productSheet?.notifyMeType !== 'OUT' && manualYellowBox && !autoYellowBox && (
            <p
              className="mb-0 mt-2.5 border border-dark px-2.5 py-2 text-sm text-copy lg:text-xs [&_a]:hover:underline"
              dangerouslySetInnerHTML={{ __html: productSheet.textDiscountCollectionOld }}
            />
          )}
          {productSheet?.notifyMeType !== 'OUT' && !recalculating && autoYellowBox && (
            <p
              className="mb-0 mt-2.5 border border-dark px-2.5 py-2 text-sm text-copy lg:text-xs [&_a]:hover:underline"
              dangerouslySetInnerHTML={{ __html: voucherPriceText }}
            />
          )}

          <div className="mt-15 empty:hidden lg:mt-10">
            <ProductBrands id={productSheet.code} products={productSheet.brandProducts} camperlab={true} />
          </div>

          <div className="mt-3 max-lg:hidden lg:mt-10">
            {Array.isArray(productSheet.sizesGuide) && productSheet.sizesGuide.length > 0 && !storeData ?
              <div className="flex justify-start text-xs uppercase">
                <SizesGuide />
              </div>
            : null}
          </div>
          <div className="bottom-0 mt-2 flex flex-col gap-[5px] bg-neutral py-2.5 max-lg:hidden lg:py-0">
            <AddToCartSection
              cartError={cartError}
              isAccessory={isAccessory}
              selectedSize={selectedSize}
              handleSelectSize={handleSelectSize}
              isBuyingDisabled={isBuyingDisabled}
              loading={loading}
              onBuy={() => handleClickAddToCartBtn(true)}
              availableSizes={availableSizes}
              availablePrice={availablePrice}
            />
          </div>
        </div>
        <div className="mt-15 lg:mt-[100px]">
          <LabProductInfoSection />
        </div>
        <div className="mt-15 lg:hidden">
          {Array.isArray(productSheet.sizesGuide) && productSheet.sizesGuide.length > 0 && !storeData ?
            <div className="flex justify-start text-xs uppercase">
              <SizesGuide />
            </div>
          : null}
        </div>
        <div
          className="sticky bottom-0 -mx-2.5 flex flex-col gap-2 bg-neutral px-2.5 py-2.5 lg:hidden"
          ref={(node) => {
            if (node) {
              addToCartWrapper.current = node;
              document.body.style.setProperty('--add-to-cart-height', `${node.offsetHeight}px`);
            }
          }}
        >
          <AddToCartSection
            cartError={cartError}
            isAccessory={isAccessory}
            selectedSize={selectedSize}
            handleSelectSize={handleSelectSize}
            isBuyingDisabled={isBuyingDisabled}
            loading={loading}
            onBuy={() => handleClickAddToCartBtn(true)}
            availableSizes={availableSizes}
            availablePrice={availablePrice}
          />
        </div>
      </div>
    </div>
  );
}

type AddToCartSectionProps = {
  cartError: string | null;
  isAccessory: boolean;
  selectedSize: string;
  handleSelectSize: (size: string) => void;
  isBuyingDisabled: boolean;
  loading: boolean;
  onBuy: () => void;
  availableSizes: string[];
  availablePrice: boolean;
};

function AddToCartSection({ cartError, isAccessory, selectedSize, handleSelectSize, isBuyingDisabled, loading, onBuy, availableSizes }: AddToCartSectionProps) {
  const { t } = useI18n();
  const { productSheet } = useProductSheet();
  const [showNotifyMe, setShowNotifyMe] = useState(false);
  const [sizesOpen, setSizesOpen] = useState(false);

  const allUnavailable = productSheet?.sizes.every((s) => s.quantity === 0) ?? false;
  const notifyMe = productSheet?.notifyMe === true;
  const notifyMeType = productSheet?.notifyMeType;
  const isNotifymeAll = notifyMe && productSheet?.notifyMeType !== 'SIZE';
  const isNotifymeBySize = notifyMe && productSheet?.notifyMeType === 'SIZE';

  let shouldOpenNotifyMe = false;
  let addToCartLabel = t('ficha.producto', 'add.to.bag', 'Add to bag');

  if (productSheet?.presales === true) {
    addToCartLabel = t('ficha.producto', 'add.to.bag.presale', 'PRE-ORDER');
  }

  if (productSheet?.membersOnly === true) {
    addToCartLabel = `${productSheet.presales === true ? t('ficha.producto', 'add.to.bag.presale') : t('ficha.producto', 'members.only.shop')} ${t('ficha.producto', 'members.only', '(Members Only)')}`;
    if (productSheet.presales === true) {
    }
  }

  if (notifyMe && notifyMeType === 'PRE') {
    addToCartLabel = t('ficha.producto', `avisame${notifyMeType}`, 'NOTIFICARME');
    shouldOpenNotifyMe = true;
  }
  if (notifyMe && notifyMeType === 'SIZE' && !availableSizes.includes(selectedSize)) {
    addToCartLabel = t('ficha.producto', `avisame${notifyMeType}`, 'NOTIFICARME');
    shouldOpenNotifyMe = true;
  }
  if (notifyMeType === 'OUT') {
    return (
      <div className="text-sm uppercase lg:text-xs">
        {t(
          'ficha.producto',
          `avisame.texto.${productSheet.notifyMeType}`,
          '***Este producto no esta a la venta en este momento, pero podemos avisarte por email en cuanto este disponible',
        )}
      </div>
    );
  }

  const onPressBuy = () => {
    if (loading === true) return;
    if (shouldOpenNotifyMe) {
      setShowNotifyMe(true);
      return;
    }
    if (selectedSize === undefined) {
      setSizesOpen(true);
      return;
    }
    onBuy();
  };

  return (
    <>
      <p className="mb-0 text-xs text-error empty:hidden">{cartError}</p>
      <div className="relative empty:hidden">
        {!isAccessory ?
          <Select
            variant="dark"
            isDisabled={loading}
            label={t('ficha.producto', 'elije.tu.talla')}
            placeholder={t('ficha.producto', 'elije.tu.talla')}
            hiddenLabel
            // items={productSheet.sizes}
            isOpen={sizesOpen}
            onOpenChange={setSizesOpen}
            selectedKey={selectedSize}
            onSelectionChange={(size) => handleSelectSize(size as string)}
          >
            {productSheet.sizes.map((size) => {
              const hasLimitedSizes = size.quantity > 0 && size.quantity < 2;
              const notAvailable = size.quantity === 0;
              let notAvailableMessage = null;
              if (notAvailable || isNotifymeAll) {
                notAvailableMessage = notifyMe ? t('ficha.producto', 'avisame.size') : t('ficha.producto', 'not.available');
              } else if (hasLimitedSizes && !isNotifymeAll) {
                notAvailableMessage = t('ficha.producto', 'size.info.only.few');
              }
              const itemDisabled = notAvailable && !isNotifymeBySize;
              return (
                <SelectItem key={size.value} id={size.value} isDisabled={itemDisabled} textValue={size.label}>
                  <span className={`flex items-baseline gap-2.5`}>
                    <span className={`${notAvailable ? 'text-disabled line-through' : ''} text-sm md:text-xs`}>{size.label}</span>
                    <span className={'text-xs empty:hidden md:text-xxs'}>{notAvailableMessage}</span>
                  </span>
                </SelectItem>
              );
            })}
          </Select>
        : null}
      </div>
      <Button isDisabled={isBuyingDisabled} isPending={loading === true} onPress={onPressBuy}>
        {addToCartLabel}
      </Button>
      {notifyMe && notifyMeType && notifyMeType !== 'OUT' ?
        <NotifyMeDrawer defaultSize={selectedSize} isModalVisible={showNotifyMe} handleCancel={() => setShowNotifyMe(false)} productSheet={productSheet} />
      : null}
    </>
    //   : <>
    //       {productSheet.notifyMe && !productSheet.recycled && availablePrice && (
    //         <div>
    //           {t(
    //             'ficha.producto',
    //             `avisame.texto.${productSheet.notifyMeType}`,
    //             '***Este producto no esta a la venta en este momento, pero podemos avisarte por email en cuanto este disponible',
    //           )}
    //         </div>
    //       )}
    //       {!productSheet.recycled && productSheet.notifyMe && availablePrice ?
    //         <NotifyMeButton defaultSize={selectedSize} code={productSheet.code} productSheet={productSheet} camperlab={true} />
    //       : <div>{t('ficha.producto', 'sin.stock')}</div>}
    //     </>
    //   }
    // </>
  );
}
